/* Repaint-recovery guard for a known Chrome GPU-compositor bug: this theme's custom full-page
   scroll container (.page-wrapper, height:100dvh; overflow-y:auto -- see assets/base.css) can,
   on some Chrome/GPU combinations, end up with the whole page painted blank white. The DOM and
   CSS are confirmed intact when this happens (elements report correct size/visibility) -- it's
   purely a paint/compositor glitch, not a logic bug.

   IMPORTANT: .page-wrapper wraps the ENTIRE page (header, main content, footer -- see
   layout/theme.liquid), not just one section. Any recovery technique that fires automatically
   and unconditionally must never be able to leave the whole page hidden.

   History:
   - v1 (2026-09-18): resize + load only, gentle overflow-toggle recovery. Confirmed (2026-09-19)
     NOT sufficient -- didn't cover in-page anchor jumps (e.g. a link to #ic-builder), which
     scroll the container programmatically without firing resize/load.
   - v2 (2026-09-19): added hashchange/visibility/safety-net triggers, and switched to a
     display:none -> forced reflow -> requestAnimationFrame restore cycle, intended to force a
     full compositor-layer rebuild. Published live and caused a NEW, WORSE bug within the same
     day: because the safety-net timers fired this display:none cycle unconditionally on every
     single page load (twice: 2s and 4s after load) across the ENTIRE site, any delay or missed
     requestAnimationFrame callback (e.g. a backgrounded tab, contention with other scripts) left
     the whole page hidden -- reported by Crystal as a totally blank ordinary product page with no
     anchor jump involved. This is what v3 exists to fix.
   - v3 (2026-09-19): keeps v2's broader trigger coverage (resize, load, hashchange,
     hash-present-on-load, visibilitychange, delayed safety-net passes) but:
       1) uses ONLY the gentle overflow-toggle technique (like v1) for every automatic trigger --
          it never hides page content, so it can never cause a blank page on its own.
       2) removes the unconditional automatic use of the aggressive display:none/reflow/restore
          technique entirely from the automatic paths. It's kept ONLY as a manually-invoked
          fallback (window.__cwForceRepaint()) for hands-on debugging, and even then it has a
          hard setTimeout safety net (in addition to requestAnimationFrame) that force-restores
          display no matter what, so it can never get stuck hidden even when triggered by hand.
   - v4 (2026-09-19): the blank-page report persisted even with v3 confirmed live, during
     ordinary scrolling on a product page (collagandwood.com/products/senior-night-
     football-ideas), which none of v3's triggers cover -- resize, hashchange and
     visibilitychange don't fire just because someone scrolls. Adds a debounced scroll listener
     on .page-wrapper itself (the actual scrolling element on this theme). Still only ever the
     safe gentleNudge() technique -- no new risk of hiding content. This was deployed alongside a
     separate, independent fix (removing a continuously-active backdrop-filter compositing layer
     from the mobile menu drawer, see snippets/header-drawer.liquid).
   - v5 (2026-09-19, this file): with v4 confirmed live, Crystal reported the blank page again --
     this time reproduced live and confirmed 100% repeatable: click "Add 20-25 photos" (opens the
     native OS file-picker for the hidden file input), then close that dialog (with or without
     picking a file) -- the page goes blank every time. Root cause: opening a native OS dialog
     blurs the browser window (fires window 'blur') and closing it re-focuses it (fires window
     'focus') WITHOUT firing visibilitychange -- the tab is still the foreground tab the whole
     time, just occluded by the OS dialog, so v3's visibilitychange trigger never sees it. None
     of v1-v4's triggers (resize, load, hashchange, visibilitychange, scroll) fire on a same-tab
     focus change like this, which is exactly why this specific, very common interaction (every
     photo upload flow on the site) was still getting through.

     Confirmed by hand (2026-09-19) that gentleNudge() -- the safe overflow-toggle technique used
     for every trigger above -- does NOT clear this particular blank state, but forceRepaint()
     (the aggressive display:none -> reflow -> restore cycle) does. So the 'focus' handler below
     is the one automatic trigger in this file that uses forceRepaint() instead of gentleNudge().
     This is deliberately NOT a repeat of v2's mistake, for two reasons: (1) v2 ran this technique
     blindly on a timer on every single page load with no real signal anything was wrong; this
     runs only when the window actually regains focus, a specific, meaningful signal tied
     directly to the confirmed cause. (2) forceRepaint() itself already carries both
     requestAnimationFrame AND a hard 120ms setTimeout fallback for restoring display (added
     in v3) -- so even in the worst case (rAF delayed or dropped), content can never stay hidden
     longer than 120ms. Debounced the same way as every other trigger in this file so rapid
     focus/blur churn (e.g. quickly alt-tabbing) can't stack up multiple overlapping cycles.
   - v6 note (2026-09-19, no change in THIS file): with v5 confirmed live, Crystal reported the
     blank page a third time -- reproduced live again, and this time the log told a different
     story: window.__cwEventLog showed NO blur, focus, or visibilitychange events at all around
     the moment it went blank, and .page-wrapper's computed display/visibility/opacity were all
     normal throughout. The blanking was happening WHILE PHOTOS WERE ACTIVELY UPLOADING -- a
     sustained few seconds of DOM/paint churn (progress re-renders, thumbnail decode/paint,
     concurrent XHRs) with no single discrete event marking it, so none of this file's
     event-based triggers (including v5's focus trigger, which only fires once the OS dialog
     has already closed) were ever positioned to catch it. Confirmed by hand again:
     window.__cwNudgeRepaint() did not clear it; window.__cwForceRepaint() did, immediately.
     Rather than add another guessed event trigger here, assets/identity-collage.js (the one
     file that actually knows when an upload is in flight) now calls window.__cwForceRepaint()
     on an 800ms interval for as long as uploadingCount() > 0, and stops itself the moment it
     hits 0. See the ensureUploadRepaintGuard() comment there for the full reasoning. This file's
     job stays the same either way: expose a safe (gentleNudge) and a hardened, self-restoring
     aggressive (forceRepaint) recovery technique for any caller, theme-wide. */
(function () {
  var pw;
  function wrapper() {
    if (!pw) pw = document.querySelector('.page-wrapper');
    return pw;
  }

  // Safe, automatic recovery: toggles overflow to force the compositor to re-evaluate the
  // scroll container's layer. Content is never hidden, so this can never blank the page.
  function gentleNudge() {
    var el = wrapper();
    if (!el) return;

    var st = el.scrollTop;
    el.scrollTop = st + 1;
    el.scrollTop = st;

    var prevOverflow = el.style.overflowY;
    el.style.overflowY = 'hidden';
    void el.offsetHeight; // force synchronous reflow
    el.style.overflowY = prevOverflow || '';
  }

  // Aggressive recovery: display:none -> forced reflow -> restore. Guarded by BOTH
  // requestAnimationFrame and a hard timeout so the container can never be left hidden even if
  // rAF is delayed or skipped. Manually invocable for hands-on debugging (window.__cwForceRepaint()
  // in the console), and also used automatically -- but ONLY from the focus trigger below, for
  // the specific, confirmed reason documented in the v5 history note above.
  function forceRepaint() {
    var el = wrapper();
    if (!el) return;

    var prevDisplay = el.style.display;
    var restored = false;
    function restore() {
      if (restored) return;
      restored = true;
      el.style.display = prevDisplay || '';
    }

    el.style.display = 'none';
    void el.offsetHeight; // force synchronous reflow
    requestAnimationFrame(restore);
    setTimeout(restore, 120); // hard safety net -- guarantees restoration even if rAF never fires
  }

  window.__cwNudgeRepaint = gentleNudge;
  window.__cwForceRepaint = forceRepaint;

  var timer;
  function scheduleNudge(delay) {
    clearTimeout(timer);
    timer = setTimeout(gentleNudge, delay);
  }

  // Window resize (the originally-reported trigger).
  window.addEventListener('resize', function () {
    scheduleNudge(120);
  });

  // Initial load.
  window.addEventListener('load', function () {
    scheduleNudge(500);
  });

  // In-page anchor jumps (e.g. a link to #ic-builder) scroll the custom container
  // programmatically, which can trigger the same class of bug independent of any resize.
  window.addEventListener('hashchange', function () {
    scheduleNudge(150);
  });
  if (window.location.hash) {
    scheduleNudge(700);
  }

  // Returning to the tab (e.g. after switching apps/tabs) is another commonly-reported
  // trigger for this class of Chrome compositor bug.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleNudge(200);
  });

  // Safety net: a couple of delayed passes after load catch cases where content inside the
  // scroll container (photos, dynamic sections) changes height shortly after the page settles,
  // without a matching resize/hashchange/visibility event. Safe to run unconditionally on every
  // page load because gentleNudge() never hides content.
  window.addEventListener('load', function () {
    scheduleNudge(2000);
    setTimeout(gentleNudge, 4000);
  });

  // Ordinary scrolling (v4, 2026-09-19): the bug has also been seen to manifest during normal
  // scrolling, which none of the above triggers cover. Listens on .page-wrapper itself -- the
  // actual scrolling element, since this theme replaces native document scrolling with a
  // custom container (see the file header comment) -- rather than window, whose scroll event
  // won't fire for this container. Uses its own debounce timer, separate from scheduleNudge's,
  // so a burst of scroll events can't cancel a pending nudge from another trigger.
  var scrollTimer;
  function onScroll() {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(gentleNudge, 150);
  }
  function bindScrollNudge() {
    var el = wrapper();
    if (el) el.addEventListener('scroll', onScroll, { passive: true });
  }
  bindScrollNudge();
  window.addEventListener('load', bindScrollNudge); // re-attempt in case .page-wrapper wasn't in the DOM yet

  // Window regaining focus (v5, 2026-09-19) -- see the history note above for the full
  // reasoning. Covers native-dialog occlusion (file pickers, date pickers, print dialogs, etc.)
  // that blurs and re-focuses the window without ever firing visibilitychange. Deliberately
  // uses forceRepaint(), not gentleNudge() -- confirmed by hand that gentleNudge alone does not
  // clear this specific blank state. Debounced like every other trigger here so rapid
  // focus/blur churn can't stack overlapping repaint cycles.
  var focusTimer;
  window.addEventListener('focus', function () {
    clearTimeout(focusTimer);
    focusTimer = setTimeout(forceRepaint, 150);
  });
})();
